import { barGeometry } from "../layout/bar_geometry";
import { packLanes } from "../layout/lane_packer";
import { markTurnaroundsByResource } from "../layout/turnarounds";
import { applyCuts, barLabel } from "./bar_cuts";
import { escapeHtml, formatDateString, parseLocalDate } from "../utils";

const MAX_LANES = 3;
const TOTAL_CELLS = 42; // 7 columns × 6 rows; covers any month layout

// A month column is roughly four times a week column while the bar is SHORTER
// (20px here, 32px there), so half a column of slant lands near 8° from the
// horizontal — a smear, not a corner. The cut is capped to hold the diagonal at
// this angle instead. Must match `.calendar-month__layer .calendar-bar`'s
// height in the stylesheet; too large a value here simply stops capping.
const BAR_HEIGHT_PX = 20;
const MIN_SLANT_DEGREES = 60;
const MAX_CUT_PX = BAR_HEIGHT_PX / Math.tan((MIN_SLANT_DEGREES * Math.PI) / 180);

// Month grid with multi-day spanning bars.
//
// Snipsnap's month view indexed each event to a single start date, which
// cannot represent a rental spanning days. Here each week row is its own
// 7-column coordinate space: a booking crossing a week boundary is split into
// one segment per row, flagged with continuesLeft/continuesRight.
//
// Month view has no vehicle axis, so bars carry a vehicle prefix. When the
// vehicle filter is active the host omits it and the customer name gets the
// room instead.
export class MonthView {
  constructor(scheduler) {
    this.scheduler = scheduler;
    this.element = document.createElement("div");
    this.element.className = "calendar-month";
    this._domListeners = [];
  }

  mount(parent) {
    parent.appendChild(this.element);
    this._render();
  }

  setData() { this._render(); }

  destroy() {
    this._tearDownDomListeners();
    this.element.remove();
  }

  _render() {
    this._tearDownDomListeners();
    this.element.innerHTML = "";
    this.element.appendChild(this._buildHeader());

    // _gridDates() returns 42 dates (a fixed 7×6 grid) including any
    // adjacent-month padding needed to fill the leading/trailing weeks.
    const dates = this._gridDates();
    for (let i = 0; i < dates.length; i += 7) {
      this.element.appendChild(this._buildWeekRow(dates.slice(i, i + 7)));
    }
  }

  _buildHeader() {
    const header = document.createElement("div");
    header.className = "calendar-month__header";
    header.style.gridTemplateColumns = "repeat(7, minmax(0, 1fr))";
    header.style.backgroundColor = "var(--calendar-header-bg)";

    const dayLabels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    dayLabels.forEach((label) => {
      const cell = document.createElement("div");
      cell.className = "calendar-month__head-cell";
      cell.textContent = label;
      header.appendChild(cell);
    });

    return header;
  }

  _buildWeekRow(days) {
    const row = document.createElement("div");
    row.className = "calendar-month__week";

    const numbers = document.createElement("div");
    numbers.className = "calendar-month__numbers";
    numbers.style.display = "grid";
    numbers.style.gridTemplateColumns = "repeat(7, 1fr)";

    const todayString = formatDateString(new Date());
    const anchorMonth = this._anchorMonth();

    days.forEach((day) => {
      const dateString = formatDateString(day);
      const cell = document.createElement("div");
      cell.className = "calendar-month__cell";
      cell.dataset.date = dateString;
      if (day.getMonth() !== anchorMonth) cell.dataset.outside = "true";
      if (dateString === todayString) cell.dataset.today = "true";
      cell.innerHTML = `<span class="calendar-month__daynum">${day.getDate()}</span>`;
      this._on(cell, "click", () =>
        this.scheduler._emit("range-select", { resourceId: null, startDate: dateString, endDate: dateString })
      );
      numbers.appendChild(cell);
    });

    row.appendChild(numbers);

    const layer = document.createElement("div");
    layer.className = "calendar-month__layer";
    row.appendChild(layer);

    this._paintWeek(layer, days);
    return row;
  }

  // Each week row is its own coordinate space, so a booking crossing a
  // boundary is clipped independently per row and flagged on the cut edge.
  _paintWeek(layer, days) {
    const range = { startDate: formatDateString(days[0]), dayCount: 7 };
    // Row is 100 percentage points wide, so each day column is 100/7 of it —
    // the same pure function the week view calls with real pixels, in a
    // different unit. Full-cell bars are one column wide at minimum, so no
    // width floor is needed in either unit.
    const colPct = 100 / 7;

    const inRange = this.scheduler.events.filter(
      (item) => barGeometry(item, range, colPct) !== null
    );

    // Grouped by vehicle before flagging — pairing across vehicles would read
    // two unrelated rentals as a handover and shorten the wrong bar. The week
    // view gets this for free by painting one resource at a time; this grid
    // mixes every vehicle into one row, so it has to group explicitly.
    const flags = markTurnaroundsByResource(inRange);

    // This grid mixes every vehicle into one row, so without the flags two
    // bookings from DIFFERENT vehicles that merely touch the same day would
    // share a lane and paint one over the other — neither gets shortened,
    // because they are not a handover.
    const { lanes, overflow } = packLanes(inRange, { maxLanes: MAX_LANES, turnarounds: flags });

    // The row is 100 percentage points wide, so the pixel cap converts through
    // its measured width. `mount()` appends before `_render()` runs, so this is
    // measurable; if it ever reads 0 the cap becomes Infinity and simply does
    // not bind, which is the previous behaviour rather than a broken one.
    const rowWidthPx = this.element.clientWidth;
    const maxCut = rowWidthPx > 0 ? (MAX_CUT_PX / rowWidthPx) * 100 : Infinity;

    lanes.forEach((lane, laneIdx) => {
      lane.forEach((item) => {
        // percentage units
        const geoPct = barGeometry(item, range, colPct, flags.get(item.id), { maxCut });
        if (!geoPct) return;

        const node = document.createElement("div");
        node.className = "calendar-bar calendar-bar--month";
        node.dataset.eventId = String(item.id);
        node.style.left = `${geoPct.left}%`;
        node.style.width = `${geoPct.width}%`;
        node.style.top = `${laneIdx * 22}px`;
        applyCuts(node, geoPct);
        node.dataset.color = item.color || "indigo";
        if (item.status === "out") node.dataset.texture = "striped";
        if (geoPct.continuesLeft) node.dataset.continuesLeft = "true";
        if (geoPct.continuesRight) node.dataset.continuesRight = "true";

        // monthTitle and title are the same value now that the hue identifies
        // the vehicle — the prefix this grid used to need is gone.
        // Percentage points here, so the pixel threshold converts through the
        // row's measured width — the same conversion maxCut uses above.
        const minPct = rowWidthPx > 0 ? (52 / rowWidthPx) * 100 : 0;
        const label = barLabel(item.title, geoPct, minPct);
        node.title = item.title;
        node.innerHTML =
          `<span class="calendar-bar__title"${label.abbreviated ? " data-abbreviated" : ""}>` +
          `${escapeHtml(label.text)}</span>`;
        this._on(node, "click", (e) => {
          e.stopPropagation();
          this.scheduler._emit("event-click", { id: item.id, kind: item.kind });
        });

        layer.appendChild(node);
      });
    });

    if (overflow.length > 0) {
      // This grid shares three lanes across the WHOLE fleet, so a busy week
      // hides bookings with no way to reach them. The week view gives every
      // vehicle its own row, which is the view that can actually show them.
      const more = document.createElement("button");
      more.type = "button";
      more.className = "calendar-month__more";
      more.style.top = `${MAX_LANES * 22}px`;
      more.textContent = `+${overflow.length} more`;
      more.title = `Show the week of ${range.startDate}, where every vehicle has its own row`;
      this._on(more, "click", (e) => {
        e.stopPropagation();
        this.scheduler._emit("overflow-open", { startDate: range.startDate });
      });
      layer.appendChild(more);
    }
  }

  _gridDates() {
    const anchor = parseLocalDate(this.scheduler.date);
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const dayOfWeek = first.getDay(); // 0 = Sun, 1 = Mon, ..., 6 = Sat
    const offsetToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const start = new Date(
      first.getFullYear(),
      first.getMonth(),
      first.getDate() - offsetToMonday
    );
    return Array.from({ length: TOTAL_CELLS }, (_, i) =>
      new Date(start.getFullYear(), start.getMonth(), start.getDate() + i)
    );
  }

  _anchorMonth() {
    return parseLocalDate(this.scheduler.date).getMonth();
  }

  _on(target, event, handler) {
    target.addEventListener(event, handler);
    this._domListeners.push({ target, event, handler });
  }

  _tearDownDomListeners() {
    this._domListeners.forEach(({ target, event, handler }) =>
      target.removeEventListener(event, handler)
    );
    this._domListeners = [];
  }
}
